import { useParams, useNavigate } from 'react-router-dom'

import type { PostKind } from '@forbiz/contracts'

import { postsApi } from '../lib/posts.js'
import { useAsync } from '../lib/useAsync.js'
import s from './Posts.module.css'

/**
 * 소식 상세 (공지·이벤트·가이드 공용). 아티클 카드 — 히어로(또는 종류 헤더 밴드) + 메타 +
 * 문단 본문 + CTA + 다른 소식. 본문이 짧아도 종류 밴드·다른 소식으로 의도적으로 채운다.
 * 본문은 평문을 문단으로 나눠 표시한다(빈 줄=문단, 줄바꿈 유지, HTML 주입 없음).
 */

function fmtDate(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`
}

const kindLabel = (k: PostKind) => (k === 'EVENT' ? '이벤트' : k === 'GUIDE' ? '이용안내' : '공지')

/** 빈 줄로 문단을 나눈다. 문단 안 단일 줄바꿈은 CSS(pre-line)로 유지. */
function paragraphs(body: string): string[] {
  return body
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
}

/** 이미지 없는 글의 상단 밴드용 라인아트 글리프 — 이모지 대신 브랜드 톤 아이콘. */
function KindGlyph({ kind }: { kind: PostKind }) {
  if (kind === 'EVENT') {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 3l1.9 3.9 4.3.6-3.1 3 .7 4.2L12 16.7 8.2 14.7l.7-4.2-3.1-3 4.3-.6L12 3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M6 20h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    )
  }
  if (kind === 'GUIDE') {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 5.5C4 4.7 4.7 4 5.5 4H11v15H5.5C4.7 19 4 18.3 4 17.5v-12z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M20 5.5C20 4.7 19.3 4 18.5 4H13v15h5.5c.8 0 1.5-.7 1.5-1.5v-12z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    )
  }
  // NOTICE — 확성기
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 10v4a1 1 0 001 1h2l3.5 3V6L7 9H5a1 1 0 00-1 1z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M15 8.5a4 4 0 010 7M17.5 6a7 7 0 010 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

export function NoticeDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data, loading, error } = useAsync(() => postsApi.get(Number(id)), [id])
  const post = data?.post

  // 다른 소식(공지·이벤트) — 현재 글 제외 최대 4개. 짧은 글의 여백을 내비게이션으로 채운다.
  const { data: feed } = useAsync(() => postsApi.list(['NOTICE', 'EVENT']), [])
  const others = (feed?.items ?? []).filter((p) => p.id !== Number(id)).slice(0, 4)

  const openLink = (link: string) => {
    if (link.startsWith('/')) navigate(link)
    else window.open(link, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className={s.page}>
      <button type="button" className={s.back} onClick={() => navigate('/notices')}>
        ‹ 소식
      </button>

      {error ? (
        <p className={s.muted}>{error}</p>
      ) : loading ? (
        <p className={s.muted}>불러오는 중…</p>
      ) : !post ? (
        <p className={s.muted}>콘텐츠를 찾을 수 없어요.</p>
      ) : (
        <>
          <article className={post.kind === 'EVENT' ? `${s.detail} ${s.detailEvent}` : s.detail}>
            {post.imageUrl ? (
              <div className={s.detailHeroWrap}>
                <img className={s.detailHero} src={post.imageUrl} alt="" />
              </div>
            ) : (
              <div className={post.kind === 'EVENT' ? `${s.kindBanner} ${s.kindBannerEvent}` : s.kindBanner}>
                <span className={s.kindGlyph}>
                  <KindGlyph kind={post.kind} />
                </span>
              </div>
            )}
            <div className={s.detailInner}>
              <div className={s.metaRow}>
                <span className={post.kind === 'EVENT' ? `${s.badge} ${s.badgeEvent}` : s.badge}>
                  {kindLabel(post.kind)}
                </span>
                <span className={s.date}>{fmtDate(post.publishedAt)}</span>
              </div>
              <h1 className={s.articleTitle}>{post.title}</h1>
              <div className={s.rule} aria-hidden="true" />
              {post.body ? (
                <div className={s.body}>
                  {paragraphs(post.body).map((p, i) => (
                    <p key={i} className={i === 0 ? s.lead : undefined}>
                      {p}
                    </p>
                  ))}
                </div>
              ) : null}
              {post.linkUrl ? (
                <button type="button" className={s.cta} onClick={() => openLink(post.linkUrl!)}>
                  {post.linkLabel?.trim() || '바로가기'} <span aria-hidden="true">→</span>
                </button>
              ) : null}
              <p className={s.help}>궁금한 점은 고객센터 1688-4318 (09~21시)로 문의해 주세요.</p>
            </div>
          </article>

          {others.length > 0 ? (
            <section className={s.related}>
              <h2 className={s.relatedHead}>다른 소식</h2>
              <ul className={s.list}>
                {others.map((p, i) => (
                  <li
                    key={p.id}
                    className={s.card}
                    style={{ '--i': i } as React.CSSProperties}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/notices/${p.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        navigate(`/notices/${p.id}`)
                      }
                    }}
                  >
                    {p.imageUrl ? (
                      <span className={s.thumb} style={{ backgroundImage: `url(${p.imageUrl})` }} aria-hidden="true" />
                    ) : null}
                    <span className={s.cardBody}>
                      <span className={s.metaRow}>
                        <span className={p.kind === 'EVENT' ? `${s.badge} ${s.badgeEvent}` : s.badge}>{kindLabel(p.kind)}</span>
                        <span className={s.date}>{fmtDate(p.publishedAt)}</span>
                      </span>
                      <span className={s.cardTitle}>{p.title}</span>
                    </span>
                    <span className={s.chevron} aria-hidden="true">›</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : (
            <button type="button" className={s.bottomBack} onClick={() => navigate('/notices')}>
              ‹ 목록으로 돌아가기
            </button>
          )}
        </>
      )}
    </div>
  )
}
